import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { fetchClergy } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Search, ChevronDown, ChevronRight, Church, Phone, Mail, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import ImageHelper from '../lib/imageHelper';

interface ClergyMember {
  id: number;
  name: string;
  title: string;
  slug: string;
  archdeaconry: string;
  parish: string;
  parish_location?: string;
  email?: string;
  phone?: string;
  rank?: string;
  photo?: string;
  hierarchy?: number;
}

const Clergy = () => {
  const [clergy, setClergy] = useState<ClergyMember[]>([]);
  const [filteredClergy, setFilteredClergy] = useState<ClergyMember[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [groupBy, setGroupBy] = useState<'archdeaconry' | 'hierarchy'>('hierarchy');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const { toast } = useToast();

  useEffect(() => {
    const loadClergy = async () => {
      try {
        const response = await fetchClergy();
        const transformed = response.data.priests.map((item: any) => {
          const primaryParish = item.parishes?.[0];
          return {
            id: item.id,
            name: item.name,
            title: item.title,
            slug: item.slug,
            archdeaconry: primaryParish?.archdeaconry?.name ?? 'Diocesan Headquarters',
            parish: primaryParish?.name ?? 'Diocese',
            parish_location: primaryParish?.location ?? '',
            email: item.email,
            phone: item.phone,
            rank: item.assignments?.[0]?.role ?? '',
            hierarchy: item.hierarchy ?? 999,
            photo: item.photo ?? 'https://res.cloudinary.com/dwrqyrm84/image/upload/t_dp_logo/v1749389564/mq1jwwqt9xxehogbwlem.png',
          };
        });
        setClergy(transformed);
        setFilteredClergy(transformed);
      } catch (error) {
        toast({ title: "Error", description: "Could not load clergy directory.", variant: "destructive" });
      }
    };
    loadClergy();
  }, [toast]);

  useEffect(() => {
    let filtered = clergy.filter(member =>
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.parish.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (groupBy === 'hierarchy') {
      filtered.sort((a, b) => (a.hierarchy ?? 999) - (b.hierarchy ?? 999));
    }
    setFilteredClergy(filtered);
  }, [clergy, searchTerm, groupBy]);

  const groupedByArch = filteredClergy.reduce((acc, member) => {
    if (!acc[member.archdeaconry]) acc[member.archdeaconry] = [];
    acc[member.archdeaconry].push(member);
    return acc;
  }, {} as Record<string, ClergyMember[]>);

  const toggleExpand = (key: string) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const ClergyCard = ({ member }: { member: ClergyMember }) => (
    <Card className="group overflow-hidden border-none shadow-sm hover:shadow-md transition-all duration-300 ring-1 ring-slate-100 bg-white">
      <div className="flex h-full">
        {/* Photo Section */}
        <div className="w-1/3 relative overflow-hidden bg-slate-100">
          <ImageHelper
            src={member.photo}
            alt={member.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
          {member.hierarchy && member.hierarchy < 10 && (
            <div className="absolute top-2 left-2 bg-amber-600 text-white p-1 rounded-full shadow-lg">
              <Award className="h-3 w-3" />
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="w-2/3 p-4 flex flex-col justify-between">
          <div>
            <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-1">
              {member.title}
            </p>
            <Link to={`/clergy/${member.slug}`}>
              <h3 className="text-lg font-serif font-bold text-slate-900 group-hover:text-amber-700 transition-colors leading-tight">
                {member.name}
              </h3>
            </Link>

            <div className="mt-3 space-y-2">
              <div className="flex items-start gap-2 text-xs text-slate-600">
                <Church className="h-3.5 w-3.5 mt-0.5 text-slate-400 shrink-0" />
                <span>{member.parish} , {member.parish_location && (<>, {member.parish_location}</>)}</span>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-4 pt-3 border-t border-slate-50">
            {member.phone && (
              <a href={`tel:${member.phone}`} className="text-slate-400 hover:text-amber-600">
                <Phone className="h-4 w-4" />
              </a>
            )}
            {member.email && (
              <a href={`mailto:${member.email}`} className="text-slate-400 hover:text-amber-600">
                <Mail className="h-4 w-4" />
              </a>
            )}
            <Link to={`/clergy/${member.slug}`} className="ml-auto text-xs font-bold text-amber-700 uppercase tracking-tighter flex items-center">
              Profile <ChevronRight className="h-3 w-3 ml-0.5" />
            </Link>
          </div>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="min-h-screen bg-[#fcfaf7] py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-serif font-bold text-slate-900 mb-4">
            Clergy Directory
          </h1>
          <div className="h-1 w-20 bg-amber-600 mx-auto mb-6"></div>

          {/* Controls Bar */}
          <div className="max-w-4xl mx-auto bg-white p-2 rounded-full shadow-sm ring-1 ring-slate-200 flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by name or parish..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="border-none focus-visible:ring-0 pl-11 rounded-full bg-transparent"
              />
            </div>
            <div className="h-8 w-[1px] bg-slate-200 hidden sm:block mx-2 self-center"></div>
            <Select value={groupBy} onValueChange={v => setGroupBy(v as 'archdeaconry' | 'hierarchy')}>
              <SelectTrigger className="w-full sm:w-48 border-none focus:ring-0 bg-transparent font-medium text-slate-700">
                <SelectValue placeholder="Sort By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hierarchy">By Seniority</SelectItem>
                <SelectItem value="archdeaconry">By Archdeaconry</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results Section */}
        {groupBy === 'hierarchy' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredClergy.map(member => (
              <ClergyCard key={member.id} member={member} />
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedByArch).map(([arch, members]) => {
              const isOpen = expandedGroups[arch] ?? true;
              return (
                <div key={arch} className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between p-5 bg-slate-50/50 hover:bg-slate-50 transition-colors"
                    onClick={() => toggleExpand(arch)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="bg-amber-600 text-white text-xs font-bold px-2 py-1 rounded">
                        {members.length}
                      </div>
                      <h2 className="text-xl font-serif font-bold text-slate-800 tracking-tight">
                        {arch} Archdeaconry
                      </h2>
                    </div>
                    {isOpen ? <ChevronDown className="h-5 w-5 text-slate-400" /> : <ChevronRight className="h-5 w-5 text-slate-400" />}
                  </button>

                  {isOpen && (
                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in slide-in-from-top-2 duration-300">
                      {members
                        .sort((a, b) => (a.hierarchy ?? 999) - (b.hierarchy ?? 999))
                        .map(member => (
                          <ClergyCard key={member.id} member={member} />
                        ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {filteredClergy.length === 0 && (
          <div className="text-center py-20">
            <p className="text-slate-400 italic">No clergy found matching your search.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Clergy;